// Public / sales-facing AI Presence tool: URL form (captcha + email when
// logged out) → POST creates a pending analysis → poll with the access
// token while an animated progress bar runs → report. Mirrors
// ConsumerVoiceTool; the run is asynchronous server-side (a quick prompt set
// asked of Claude, Gemini and OpenAI with web search, a few minutes).
import React, { useCallback, useEffect, useRef, useState } from 'react';
import axiosInstant from '../../api';
import { apiErrorCode } from '../../utils/apiError';
import { PdpAuditForm, PdpAuditFormSubmitArgs } from '../PdpAudit/PdpAuditForm';
import { AuditDisclaimer, PortalCta } from '../PdpAudit/PdpAuditTool';
import FunnelChoiceCard from '../FunnelCta/FunnelChoiceCard';
import { buildRegisterHandoffPath, buildStrategyPath } from '../../utils/funnelHandoff';
import { useUser } from '../../hooks/useUser';
import { aiPresenceResponsesEndpoint, auditAiPresenceEndpoint, auditAiPresenceStatusEndpoint } from '../../api/endpoints';
import { AiPresenceAudit } from '../../types/aiPresence';
import { AiPresenceReportView } from './AiPresenceReportView';
import { buildAiPresencePdfBlob, aiPresenceFileName } from './aiPresenceFiles';
import { downloadBlob } from '../PdpAudit/reportFiles';
import { BLACK, BORDER, FONT, GOLD, GOLD_LIGHT, PAGE_BG, SURFACE_2, TEXT_DISCLAIMER, TEXT_FAINT, TEXT_MUTED } from '../ProposalFlow/theme';

const POLL_MS = 5000;
const PROGRESS_MAX_DURATION_MS = 120000;
const DEFAULT_HEADING = 'AI Presence Analysis';

type PageState =
  | { kind: 'idle' }
  | { kind: 'submitting'; url: string }
  | { kind: 'polling'; url: string; id: number; token: string }
  | { kind: 'success'; audit: AiPresenceAudit; token: string }
  | { kind: 'error'; message: string };

function mapError(err: any): string {
  const status = err?.response?.status;
  const code = apiErrorCode(err);
  if (code === 'rate_limit_exceeded' || status === 429) {
    return err?.response?.data?.message || 'Too many analyses from this network. Please try again later.';
  }
  if (code === 'CaptchaFailed') return 'The captcha check failed. Please try again.';
  if (code === 'InvalidUrl') return "We couldn't read that URL. Double-check it points to a public product page.";
  if (code === 'EmailRequired') return 'Please enter a company email address to run the analysis.';
  if (code === 'EmailDomainNotAllowed') return 'Please use a company email address — consumer providers (gmail, yahoo, etc.) aren\'t supported.';
  return err?.response?.data?.message || 'Something went wrong starting your analysis. Please try again.';
}

const STEPS = [
  { label: 'Reading the product page', start: 0, end: 12 },
  { label: 'Writing the shopping questions buyers ask', start: 12, end: 25 },
  { label: 'Asking Claude, Gemini and OpenAI', start: 25, end: 75 },
  { label: 'Checking mentions, recommendations and claims', start: 75, end: 92 },
  { label: 'Writing the report', start: 92, end: 100 },
];

function Progress({ url, done }: { url: string; done: boolean }) {
  const [progress, setProgress] = useState(0);
  const startRef = useRef(Date.now());
  useEffect(() => {
    if (done) {
      setProgress(100);
      return undefined;
    }
    const id = setInterval(() => {
      const t = Math.min((Date.now() - startRef.current) / PROGRESS_MAX_DURATION_MS, 1);
      setProgress(96 * (1 - (1 - t) ** 2.2));
    }, 120);
    return () => clearInterval(id);
  }, [done]);
  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', paddingTop: 8 }}>
      <div style={{ fontFamily: FONT, fontSize: 16, fontWeight: 600, color: BLACK, marginBottom: 6, textAlign: 'center' }}>
        Asking AI what shoppers should buy
      </div>
      <div style={{ fontFamily: FONT, fontSize: 13, color: TEXT_FAINT, marginBottom: 20, textAlign: 'center', wordBreak: 'break-all' }}>{url}</div>
      <div style={{ width: '100%', height: 8, background: SURFACE_2, borderRadius: 4, overflow: 'hidden' }}>
        <div style={{ height: '100%', width: `${progress}%`, background: `linear-gradient(90deg, ${GOLD}, ${GOLD_LIGHT})`, borderRadius: 4, transition: 'width 0.2s ease-out' }} />
      </div>
      <div style={{ display: 'flex', justifyContent: 'space-between', width: '100%', marginTop: 8 }}>
        <span style={{ fontFamily: FONT, fontSize: 13, fontWeight: 600, color: GOLD }}>{Math.round(progress)}%</span>
        <span style={{ fontFamily: FONT, fontSize: 12, color: TEXT_DISCLAIMER }}>This usually takes 1 to 2 minutes</span>
      </div>
      <div style={{ marginTop: 24, display: 'flex', flexDirection: 'column', gap: 8, width: '100%' }}>
        {STEPS.map((s) => {
          let status: 'done' | 'active' | 'pending' = 'pending';
          if (progress >= s.end) status = 'done';
          else if (progress >= s.start) status = 'active';
          return (
            <div key={s.label} style={{ fontFamily: FONT, fontSize: 13, color: status === 'pending' ? TEXT_FAINT : BLACK, display: 'flex', gap: 10, alignItems: 'center' }}>
              <span style={{ width: 10, height: 10, borderRadius: '50%', background: status === 'done' ? GOLD : 'transparent', border: `1.5px solid ${status === 'pending' ? BORDER : GOLD}`, display: 'inline-block' }} />
              {s.label}
            </div>
          );
        })}
      </div>
    </div>
  );
}

export interface AiPresenceToolProps {
  showCaptcha: boolean;
  requireEmail: boolean;
  showPortalCta?: boolean;
  heading?: string;
  bgColor?: string;
  // Where the report's Create strategy CTA goes; gets ?aip=<id>&t=<token>.
  strategyPath?: string;
  onReportCreated?: (audit: AiPresenceAudit) => void;
}

export function AiPresenceTool({
  showCaptcha,
  requireEmail,
  showPortalCta = false,
  heading = DEFAULT_HEADING,
  bgColor = PAGE_BG,
  strategyPath,
  onReportCreated,
}: AiPresenceToolProps) {
  const [state, setState] = useState<PageState>({ kind: 'idle' });
  // The email the visitor ran this with: rides into the strategy and
  // register links so neither asks for it again.
  const [submittedEmail, setSubmittedEmail] = useState('');
  const { user } = useUser();
  const storeEmail =
    ((user as any)?.emailAddress as string | undefined) || ((user as any)?.email_address as string | undefined) || '';
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => () => {
    if (timerRef.current) clearTimeout(timerRef.current);
  }, []);

  const poll = useCallback(
    (id: number, token: string, url: string) => {
      const tick = async () => {
        try {
          const { data } = await axiosInstant.get<AiPresenceAudit>(auditAiPresenceStatusEndpoint(id, token));
          if (data.status === 'ready') {
            setState({ kind: 'success', audit: data, token });
            onReportCreated?.(data);
            return;
          }
          if (data.status === 'error') {
            setState({
              kind: 'error',
              message:
                data.generation_error === 'NoLlmApiKey'
                  ? 'AI Presence analysis is not configured on this environment.'
                  : "We couldn't complete the analysis for that page. Try a different product URL.",
            });
            return;
          }
        } catch {
          // transient — keep polling
        }
        timerRef.current = setTimeout(tick, POLL_MS);
      };
      setState({ kind: 'polling', url, id, token });
      timerRef.current = setTimeout(tick, POLL_MS);
    },
    [onReportCreated],
  );

  const onSubmit = useCallback(
    async ({ url, captchaToken, email }: PdpAuditFormSubmitArgs) => {
      const effectiveEmail = (email || storeEmail || '').trim().toLowerCase();
      setSubmittedEmail(effectiveEmail);
      setState({ kind: 'submitting', url });
      try {
        const { data } = await axiosInstant.post<{ id: number; access_token: string; status: string }>(
          auditAiPresenceEndpoint,
          { url, captchaToken, email: effectiveEmail || undefined },
        );
        poll(data.id, data.access_token, url);
      } catch (err: any) {
        setState({ kind: 'error', message: mapError(err) });
      }
    },
    [poll, storeEmail],
  );

  const download = async (audit: AiPresenceAudit) => {
    downloadBlob(await buildAiPresencePdfBlob(audit), aiPresenceFileName(audit));
  };

  const shell = (children: React.ReactNode) => (
    <div style={{ minHeight: '100vh', background: bgColor, fontFamily: FONT }}>
      <style>{`@import url('https://fonts.googleapis.com/css2?family=DM+Sans:opsz,wght@9..40,400;9..40,500;9..40,600;9..40,700&display=swap');`}</style>
      {children}
    </div>
  );

  const head = (
    <>
      <h1 style={{ fontFamily: FONT, fontSize: 32, fontWeight: 700, color: BLACK, letterSpacing: '-0.3px', margin: '0 0 10px' }}>{heading}</h1>
      <p style={{ fontFamily: FONT, fontSize: 15, color: TEXT_MUTED, lineHeight: 1.6, margin: '0 0 32px' }}>
        When shoppers ask AI what to buy, does your product show up, and does AI recommend it? Drop in a product
        page URL and we&apos;ll ask Claude, Google Gemini and OpenAI the shopping questions your buyers ask: an AI
        Presence Score per platform, the competitors AI names instead, the sources it cites, and what to fix.
      </p>
    </>
  );

  if (state.kind === 'success') {
    const { audit, token } = state;
    const subject = ((audit.report as any) && (audit.report as any).subject) || {};
    const analysis = { id: audit.id, token };
    const strategyHref = buildStrategyPath(strategyPath || '/proposal', {
      title: subject.product_name || audit.params.product_name,
      url: audit.url,
      brand: subject.brand_name || audit.params.brand_name,
      email: submittedEmail || storeEmail,
      aiPresence: analysis,
    });
    // Anonymous visitors get the register / strategy choice instead of the
    // report's own strategy card (lib/funnelClaim claims the analysis by
    // its token and imports the product).
    const choice = showPortalCta ? (
      <FunnelChoiceCard
        registerHref={buildRegisterHandoffPath({ email: submittedEmail, url: audit.url, aiPresence: analysis })}
        strategyHref={strategyHref}
      />
    ) : null;
    return shell(
      <div style={{ width: 860, maxWidth: '100%', margin: '0 auto', padding: '32px 20px' }}>
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginBottom: 8 }}>
          <button
            type="button"
            onClick={() => download(audit)}
            style={{ fontFamily: FONT, fontSize: 12, fontWeight: 600, color: BLACK, border: `1px solid ${BORDER}`, background: 'transparent', borderRadius: 8, padding: '8px 12px', cursor: 'pointer' }}
          >
            Download PDF
          </button>
          <button
            type="button"
            onClick={() => setState({ kind: 'idle' })}
            style={{ fontFamily: FONT, fontSize: 12, fontWeight: 600, color: BLACK, border: `1px solid ${BORDER}`, background: 'transparent', borderRadius: 8, padding: '8px 12px', cursor: 'pointer' }}
          >
            Analyze another product
          </button>
        </div>
        {choice}
        <div style={{ background: 'var(--tool-card, #fff)', border: `1px solid ${BORDER}`, borderRadius: 16 }}>
          <AiPresenceReportView
            audit={audit}
            responsesEndpoint={(promptKey) => aiPresenceResponsesEndpoint(auditAiPresenceStatusEndpoint(audit.id, token), promptKey)}
            onCreateStrategy={strategyPath && !showPortalCta ? () => window.location.assign(strategyHref) : undefined}
          />
        </div>
        {choice}
        <AuditDisclaimer />
      </div>,
    );
  }

  return shell(
    <div style={{ width: 640, maxWidth: '100%', margin: '0 auto', padding: '48px 20px' }}>
      {head}
      {state.kind === 'error' && (
        <div style={{ fontFamily: FONT, fontSize: 13, color: '#c62828', background: '#FBEFEF', border: '1px solid #f3cfcf', borderRadius: 10, padding: '10px 14px', marginBottom: 16 }}>
          {state.message}
        </div>
      )}
      {state.kind === 'polling' || state.kind === 'submitting' ? (
        <Progress url={state.url} done={false} />
      ) : (
        <PdpAuditForm onSubmit={onSubmit} showCaptcha={showCaptcha} requireEmail={requireEmail} submittingLabel="Starting analysis…" />
      )}
      {state.kind === 'idle' && showPortalCta && <PortalCta />}
      <AuditDisclaimer />
    </div>,
  );
}
